import { Optional, Pipe, PipeTransform } from '@angular/core';
import { MmuUserService } from '../../auth/user-service/mmu-user.service';
import { MmuFormatNumberPipe } from '../format-number/format-number.pipe';
import { EnergyConversion, MmuEnergyPipe } from './energy.pipe';


/**
 * @see EnergyConversion
 */
export enum EnergyUnit {
  WH = 'WH',
  KWH = 'KWH'
}

const energyUnitsMapping: Record<string, EnergyUnit> = {
  [EnergyUnit.WH + '-' + EnergyUnit.KWH]: EnergyUnit.KWH,
  [EnergyUnit.KWH + '-' + EnergyUnit.WH]: EnergyUnit.WH
};

/**
 * This pipe is used to format volumes according to the user's preferred format.
 */
@Pipe({
  name: 'formatEnergy',
  pure: true
})
export class MmuFormatEnergyPipe implements PipeTransform {
  private energyPipe: MmuEnergyPipe;
  private formatNumberPipe: MmuFormatNumberPipe;
  private readonly energyFormat: EnergyUnit;

  constructor(@Optional() private mmuUserService: MmuUserService) {
    if (this.mmuUserService === null || this.mmuUserService === undefined) {
      this.formatNumberPipe = new MmuFormatNumberPipe();
    } else {
      this.formatNumberPipe = new MmuFormatNumberPipe(mmuUserService);
    }
    this.energyFormat = EnergyUnit.KWH;
    this.energyPipe = new MmuEnergyPipe();
  }

  transform(value: number, inputUnit: EnergyUnit, digitsInfo?: string): string {
    const outputUnit: EnergyUnit = this.determineOutputUnit(inputUnit);
    const convertedValue: number = this.convertValue(value, inputUnit, outputUnit);
    const valueString: string = this.formatNumberPipe.transform(convertedValue, digitsInfo);
    const unitString: string = this.unitString(outputUnit);

    return valueString + ' ' + unitString;
  }

  private determineOutputUnit(inputUnit: EnergyUnit): EnergyUnit {
    const outputUnit: EnergyUnit = energyUnitsMapping[inputUnit + '-' + this.energyFormat];
    return outputUnit ?? inputUnit;
  }

  private convertValue(value: number, inputUnit: EnergyUnit, outputUnit: EnergyUnit): number {
    if (inputUnit === outputUnit) {
      return value;
    }

    const conversion = EnergyConversion[inputUnit + '_' + outputUnit as keyof typeof EnergyConversion];
    return this.energyPipe.transform(value, conversion);
  }

  private unitString(outputUnit: EnergyUnit): 'Wh' | 'kWh' {
    switch (outputUnit) {
      case EnergyUnit.WH:
        return 'Wh';
      case EnergyUnit.KWH:
        return 'kWh';
    }
  }
}
